import dayjs from 'dayjs';
import { P } from '../theme/tokens';

export const TONE = {
  danger: P.danger,
  warning: P.warning,
  info: P.info,
  cyan: P.cyan,
  purple: P.purple,
  orange: '#D2711E',
  success: P.success,
  default: P.subtle
};

export const TICKET_STATUS = {
  new: { label: 'New', color: P.info },
  in_progress: { label: 'In progress', color: P.info },
  on_scene: { label: 'On scene', color: P.success },
  scheduled: { label: 'Scheduled', color: P.purple },
  awaiting_parts: { label: 'Awaiting parts', color: P.warning },
  owner_approval: { label: 'Owner approval', color: P.warning },
  resolved: { label: 'Resolved', color: P.subtle }
};

export const TIERS = {
  flood: { label: 'Flood', tone: 'danger' },
  fire: { label: 'Fire', tone: 'danger' },
  emergency: { label: 'Urgent', tone: 'danger' },
  water: { label: 'Urgent', tone: 'danger' },
  standard: { label: 'Standard', tone: 'default' },
  routine: { label: 'Routine', tone: 'default' }
};

export const TIER_OPTIONS = [
  { value: 'standard', label: 'Standard' },
  { value: 'routine', label: 'Routine' },
  { value: 'emergency', label: 'Urgent' },
  { value: 'flood', label: 'Flood' },
  { value: 'fire', label: 'Fire' }
];

export const STATUSES = [
  { label: 'Unassigned', tone: 'warning', value: 'new' },
  { label: 'Scheduled', tone: 'info', value: 'scheduled' },
  { label: 'In progress', tone: 'cyan', value: 'in_progress' },
  { label: 'Awaiting parts', tone: 'purple', value: 'awaiting_parts' },
  { label: 'Owner approval', tone: 'orange', value: 'owner_approval' },
  { label: 'Resolved', tone: 'success', value: 'resolved' }
];

const STATUS_BY_LABEL = Object.fromEntries(STATUSES.map((s) => [s.label, s]));
const LABEL_BY_VALUE = { ...Object.fromEntries(STATUSES.map((s) => [s.value, s.label])), on_scene: 'In progress' };

export const EMERGENCY = 'Emergency';

export const TIER_FILTERS = [
  { key: 'all', label: 'All Tickets' },
  { key: 'emergency', label: 'Emergency' },
  { key: 'standard', label: 'Standard & Routine' }
];

export const CADENCES = [
  { value: '', label: 'One time', step: null },
  { value: 'weekly', label: 'Weekly', step: [1, 'week'] },
  { value: 'biweekly', label: 'Every 2 weeks', step: [2, 'week'] },
  { value: 'monthly', label: 'Monthly', step: [1, 'month'] },
  { value: 'quarterly', label: 'Quarterly', step: [3, 'month'] },
  { value: 'semiannual', label: 'Every 6 months', step: [6, 'month'] },
  { value: 'annual', label: 'Yearly', step: [1, 'year'] }
];

const CADENCE = Object.fromEntries(CADENCES.map((c) => [c.value, c]));

const statusTone = (label) => STATUS_BY_LABEL[label]?.tone || 'default';
export const statusValue = (label) => STATUS_BY_LABEL[label]?.value || 'new';
export const cadenceLabel = (value) => CADENCE[value]?.label || '';
export const isEmergency = (t) => t.tierTone === 'danger';
export const isOpen = (t) => t.status !== 'Resolved';
export const vendorIdOf = (v) => (v && typeof v === 'object' ? v._id : v) || '';

const statusLabel = (t) =>
  t.status === 'new' && t.assignedTo?.trim() ? 'Scheduled' : LABEL_BY_VALUE[t.status] || 'Unassigned';

export function toTicket(t) {
  const tier = TIERS[t.tier] || TIERS.standard;
  const status = statusLabel(t);
  return {
    id: t._id,
    number: t.ticketNumber || '',
    by: t.by || '',
    callId: t.callRecordId || null,
    property: t.propertyUnit || '',
    issue: t.issue || 'Untitled ticket',
    tierKey: t.tier || 'standard',
    tier: tier.label,
    tierTone: tier.tone,
    status,
    statusTone: statusTone(status),
    assigned: t.assignedTo?.trim() || '',
    vendorId: vendorIdOf(t.vendorId),
    scheduleWindow: t.scheduleWindow || '',
    scheduledFor: t.scheduledFor || null,
    opened: t.createdAt,
    ownerApproval: t.ownerApproval || {},
    notes: t.noteThread || []
  };
}

export function toRecurring(r) {
  return {
    id: r._id,
    property: r.property || '',
    service: r.service || 'Service',
    cadence: r.cadence || '',
    nextDate: r.nextDate || null,
    vendor: r.vendorId?.company || '',
    vendorId: vendorIdOf(r.vendorId),
    notes: r.notes || ''
  };
}

export function occurrences(rule, start, end) {
  if (!rule.nextDate) return [];
  const step = CADENCE[rule.cadence]?.step;
  let d = dayjs(rule.nextDate);
  if (!step) return d.isBefore(start) || d.isAfter(end) ? [] : [d];
  while (d.isBefore(start)) d = d.add(...step);
  const out = [];
  while (!d.isAfter(end)) {
    out.push(d);
    d = d.add(...step);
  }
  return out;
}

export const timeOf = (d, fallback = 'All day') =>
  d.hour() === 0 && d.minute() === 0 ? fallback : d.format('h:mm A');

export function ageOf(requested) {
  const created = new Date(requested).getTime();
  const diff = Date.now() - created;
  const mins = Math.floor(diff / 60000);
  const hrs = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 60) return { text: `${Math.max(1, mins)}m ago`, tone: 'default' };
  if (hrs < 24) return { text: `${hrs}h ago`, tone: 'default' };
  if (days < 7) return { text: `${days}d ago`, tone: days >= 3 ? 'warning' : 'info' };
  return {
    text: new Date(created).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    tone: 'danger'
  };
}
